import type { ClickToTradePreviewOrder } from '@src/components/MarketDataCard/types';
import { TradeDirection } from '@src/components/TradeDirection';
import { ORDER_SIDES } from '@src/tokens/order';
import { getExpectedFillPriceAndSize } from '@src/utils/getExpectedFillPriceAndSize';
import { getMarketDataLevelPrices } from '@src/utils/getMarketDataLevelPrice';
import {
  AggressorFlagEnum,
  MixpanelEventSource,
  NoDataMessage,
  SideEnum,
  StackedFormattedNumber,
  getSpreadUnit,
  useObservableValue,
  useSecurity,
  type MarketDataSnapshot,
  type OrderIntent,
  type Security,
  type TradeDirection as TradeDirectionType,
  type WebsocketError,
} from '@talos/kyoko';
import { get } from 'lodash-es';
import { memo, useCallback, useMemo, type CSSProperties, type HTMLAttributes, type MouseEvent } from 'react';
import type { Observable } from 'rxjs';
import { of } from 'rxjs';
import { useGetOrderCurrency } from '../OMS/useGetOrderCurrency';
import { AdditionalText, Bucket, Content, Indicator, Label, Price, PriceWrapper, Wrapper } from './styles';

export interface TopOfBookProps extends Omit<HTMLAttributes<HTMLDivElement>, 'security'> {
  security?: Security;
  topOfBookSizeBucket?: string;
  topOfBookSnapshots: Observable<MarketDataSnapshot>;
  bboSnapshots?: Observable<MarketDataSnapshot>; // Best Bid Offer, for Depth: 1
  alwaysShowBestBidOffer?: boolean;
  tradeDirections: Observable<TradeDirectionType>;
  onClickPrice?: (args: {
    side: SideEnum;
    price?: string;
    currency?: string;
    orderQty?: string;
    expectedFillPrice?: string;
    expectedFillQty?: string;
    intent: OrderIntent;
  }) => void;
  onMouseLeavePrice?: (args?: any) => void;
  onUpdateSize?: (index: number, newSize: string) => void;
  isReadOnly?: boolean;
  showQuantity?: boolean;
  showSideLabels: string;
  activeOrder?: ClickToTradePreviewOrder | undefined;
  style?: CSSProperties;
  showAllInPrices?: boolean; // TODO not sure if this is actually a parameter?
  additionalLabel?: string;
  marketDataErrors?: Observable<WebsocketError | undefined>;
  isPricingModeBPS?: boolean;
}

export const TopOfBook = memo(function TopOfBook({
  security: fallbackSecurity,
  topOfBookSizeBucket,
  topOfBookSnapshots,
  bboSnapshots, // Top of book, Depth: 1
  alwaysShowBestBidOffer,
  tradeDirections,
  onClickPrice = () => {},
  onMouseLeavePrice,
  onUpdateSize,
  isReadOnly = false,
  showQuantity = true,
  showSideLabels = 'hover',
  activeOrder,
  additionalLabel = '',
  marketDataErrors,
  isPricingModeBPS,
  ...props
}: TopOfBookProps) {
  const topOfBookSnapshot = useObservableValue(() => topOfBookSnapshots, [topOfBookSnapshots]);
  const topOfBookSymbol = get(topOfBookSnapshot, 'Symbol');
  // Ideally, we use the security from the topOfBookSnapshot, but if it's not available, we use the fallbackSecurity
  // This is to ensure that the displayed offer/bid prices are formatted correctly, and not changed prematurely
  // (the security can change before we receive data for the top of book snapshot)
  const security = useSecurity(topOfBookSymbol) ?? fallbackSecurity;
  const { MinimumSize, DefaultSizeIncrement, PriceDisplaySpec, QuoteCurrency } = security || {};
  const differenceUnit = getSpreadUnit(security, isPricingModeBPS);
  const bboSnapshot = useObservableValue(() => (bboSnapshots ? bboSnapshots : of(null)), [bboSnapshots]);
  const tradeDirection = useObservableValue(() => tradeDirections, [tradeDirections]);

  const topOfBook = getMarketDataLevelPrices({ snapshot: topOfBookSnapshot, index: 0, isPricingModeBPS });

  const useBboSnapshot = bboSnapshot && (alwaysShowBestBidOffer || (!topOfBook.bidPrice && !topOfBook.offerPrice));

  const { bidPrice: bid, offerPrice: offer } = useBboSnapshot
    ? getMarketDataLevelPrices({ snapshot: bboSnapshot, index: 0, isPricingModeBPS })
    : topOfBook;

  const { getOrderCurrency } = useGetOrderCurrency();

  const additionalLabelStr = useMemo(
    () => (useBboSnapshot && bid && offer ? 'BEST BID OFFER' : additionalLabel),
    [additionalLabel, useBboSnapshot, bid, offer]
  );

  const handleClickItem = useCallback(
    (e: MouseEvent, side: SideEnum, intent: OrderIntent) => {
      e.preventDefault();
      if (side === SideEnum.Buy && offer == null) {
        return;
      }
      if (side === SideEnum.Sell && bid == null) {
        return;
      }
      const currency = getOrderCurrency(security, side);
      const { expectedFillQty, expectedFillPrice } = getExpectedFillPriceAndSize({
        security,
        orderCurrency: currency,
        marketDataSnapshot: useBboSnapshot ? bboSnapshot : topOfBookSnapshot,
        orderSide: side,
        orderQty: topOfBookSizeBucket,
      });
      const price =
        side === ORDER_SIDES.BUY
          ? intent === AggressorFlagEnum.Passive
            ? bid
            : offer
          : intent === AggressorFlagEnum.Passive
            ? offer
            : bid;
      onClickPrice({
        side,
        price,
        currency,
        orderQty: topOfBookSizeBucket,
        expectedFillPrice,
        expectedFillQty,
        intent,
      });
    },
    [
      topOfBookSnapshot,
      bboSnapshot,
      topOfBookSizeBucket,
      useBboSnapshot,
      bid,
      offer,
      onClickPrice,
      security,
      getOrderCurrency,
    ]
  );

  const marketDataError = useObservableValue(() => marketDataErrors || of(undefined), [marketDataErrors]);

  const showBboHighlight = !alwaysShowBestBidOffer && useBboSnapshot === true && bid != null && offer != null;

  const lhsActive =
    activeOrder?.source === MixpanelEventSource.TopOfBook &&
    ((activeOrder.side === SideEnum.Buy && activeOrder.intent === AggressorFlagEnum.Passive) ||
      (activeOrder.side === SideEnum.Sell && activeOrder.intent === AggressorFlagEnum.Active));

  const rhsActive =
    activeOrder?.source === MixpanelEventSource.TopOfBook &&
    ((activeOrder.side === SideEnum.Sell && activeOrder.intent === AggressorFlagEnum.Passive) ||
      (activeOrder.side === SideEnum.Buy && activeOrder.intent === AggressorFlagEnum.Active));

  return (
    <Wrapper {...props} highlightBBO={showBboHighlight}>
      {marketDataError && <NoDataMessage>{marketDataError.msg}</NoDataMessage>}
      {!marketDataError && (
        <Content>
          <PriceWrapper
            active={lhsActive}
            side={activeOrder?.side ?? SideEnum.Sell}
            isReadOnly={isReadOnly}
            onClick={e => !isReadOnly && handleClickItem(e, SideEnum.Sell, AggressorFlagEnum.Active)}
            onContextMenu={e => !isReadOnly && handleClickItem(e, SideEnum.Buy, AggressorFlagEnum.Passive)}
            onMouseLeave={onMouseLeavePrice}
            data-testid="top-of-book-bid"
          >
            <Price>
              <StackedFormattedNumber number={bid} specification={PriceDisplaySpec} showBPSLabel={isPricingModeBPS} />
            </Price>
            <Label showLabel={showSideLabels}>{activeOrder?.side === SideEnum.Buy ? 'Bid' : 'Sell'}</Label>
          </PriceWrapper>
          <Indicator>
            <TradeDirection
              bid={bid}
              offer={offer}
              direction={(tradeDirection && tradeDirection.Dir) || 0}
              differenceUnit={differenceUnit}
              priceUnit={QuoteCurrency}
              showShadow
              isPricingModeBPS={isPricingModeBPS}
            />
            {additionalLabelStr && (bid || offer) ? (
              <AdditionalText data-testid="bbo-label">{additionalLabelStr}</AdditionalText>
            ) : null}
            {showQuantity && (
              <Bucket
                shouldExpand={false}
                increment={DefaultSizeIncrement}
                index={0}
                min={MinimumSize}
                rawValue={topOfBookSizeBucket}
                onUpdateSize={onUpdateSize}
                isLarge={true}
                width="100%"
                // Hardcoded until we migrate to use a standard input as base
                style={{ height: '24px' }}
                shouldShowFullValue={true}
                shouldHighlightOnClick={false}
                precision={1}
              />
            )}
          </Indicator>
          <PriceWrapper
            active={rhsActive}
            side={activeOrder?.side ?? SideEnum.Buy}
            isReadOnly={isReadOnly}
            onClick={e => !isReadOnly && handleClickItem(e, SideEnum.Buy, AggressorFlagEnum.Active)}
            onContextMenu={e => !isReadOnly && handleClickItem(e, SideEnum.Sell, AggressorFlagEnum.Passive)}
            onMouseLeave={onMouseLeavePrice}
            data-testid="top-of-book-offer"
          >
            <Price>
              <StackedFormattedNumber number={offer} specification={PriceDisplaySpec} showBPSLabel={isPricingModeBPS} />
            </Price>
            <Label showLabel={showSideLabels}>{activeOrder?.side === SideEnum.Sell ? 'Offer' : 'Buy'}</Label>
          </PriceWrapper>
        </Content>
      )}
    </Wrapper>
  );
});
